import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { toast } from "sonner";
import {
  ShieldWarning, MapPin as PinIcon, WarningOctagon, Confetti, Car,
  ArrowFatUp, Trash, Upload, X as CloseIcon, ImageSquare, VideoCamera, Newspaper,
  ChatCircle, PaperPlaneRight, Globe,
} from "@phosphor-icons/react";

import Navbar from "@/components/Navbar";
import MediaLightbox from "@/components/MediaLightbox";
import { useAuth } from "@/lib/auth";
import { api, API, GEORGETOWN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// Resolve the public profile URL for any post/comment author. Admins have no
// public profile, so we return null and callers render plain text.
function profileHref(author) {
  if (!author || !author.user_id) return null;
  if (author.user_role === "driver") return `/d/${author.user_id}`;
  if (author.user_role === "rider") return `/r/${author.user_id}`;
  return null;
}

// ---------- Post-type metadata ----------
// `TYPES` powers the composer's type picker. `safety` is intentionally
// excluded — those posts are system-generated by the 120km/h speed alert
// pipeline and users can't create them manually.
const TYPES = [
  { key: "news",   label: "News",   color: "#E11D48", icon: Newspaper, hint: "Share something happening in Guyana" },
  { key: "police", label: "Police", color: "#1d4ed8", icon: ShieldWarning, ttl: "2h", hint: "Warn drivers about cops nearby" },
  { key: "place",  label: "Place",  color: "#00A859", icon: PinIcon,   hint: "Pin a landmark or business" },
  { key: "hazard", label: "Hazard", color: "#FF8800", icon: WarningOctagon, ttl: "6h", hint: "Flag a road issue" },
  { key: "event",  label: "Event",  color: "#FFB800", icon: Confetti,  ttl: "24h", hint: "Tell people what's on" },
];
// Safety metadata is registered so filter/pin/badge lookups work even
// though it's not a user-selectable composer option.
const SAFETY_TYPE = { key: "safety", label: "Safety", color: "#E11D48", icon: WarningOctagon, ttl: "24h", hint: "Auto speed alerts" };
const FILTERS = [{ key: "all", label: "All", color: "#111", icon: Globe }, ...TYPES, SAFETY_TYPE];
const TYPE_BY_KEY = Object.fromEntries(FILTERS.map((t) => [t.key, t]));

function buildPinIcon(type) {
  const t = TYPE_BY_KEY[type] || TYPE_BY_KEY.place;
  const glyph = type === "police" ? "🚓" : type === "hazard" ? "⚠️"
    : type === "safety" ? "🚨"
    : type === "event" ? "🎉" : type === "news" ? "📰" : "📍";
  return L.divIcon({
    className: "",
    html: `<div style="width:32px;height:32px;background:${t.color};border:3px solid #111;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-size:15px;box-shadow:2px 2px 0 #111;">${glyph}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

const mediaUrl = (path) => `${API}/public/media/${path}`;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [showMap, setShowMap] = useState(false); // mobile/desktop toggle
  const [mapCenter, setMapCenter] = useState(GEORGETOWN);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?type=${filter}` : "";
      const { data } = await api.get(`/community/posts${params}`);
      setPosts(data || []);
    } catch {
      toast.error("Couldn't load feed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  // Try to center the side map on the user's location.
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 60000, timeout: 5000 },
    );
  }, []);

  const upvote = async (postId) => {
    if (!user) { toast.error("Sign in to like"); return; }
    setPosts((prev) =>
      prev.map((p) =>
        p.post_id === postId
          ? { ...p, upvoted: !p.upvoted, upvotes: p.upvotes + (p.upvoted ? -1 : 1) }
          : p
      )
    );
    try { await api.post(`/community/posts/${postId}/upvote`); }
    catch { toast.error("Like failed"); load(); }
  };

  const removePost = async (postId) => {
    if (!confirm("Delete this post?")) return;
    try {
      await api.delete(`/community/posts/${postId}`);
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
      toast.success("Post removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  const pinnable = useMemo(
    () => posts.filter((p) => p.location?.lat != null && p.location?.lng != null),
    [posts]
  );

  return (
    <div className="min-h-screen bg-card text-foreground">
      <Navbar active="community" />

      {/* Sticky filter strip — Facebook-style story bar */}
      <div className="sticky top-14 z-20 bg-card/95 backdrop-blur border-b-2 border-foreground" data-testid="community-filter-strip">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-6 py-2 flex items-center gap-2 overflow-x-auto">
          {FILTERS.map((t) => {
            const Icon = t.icon;
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                data-testid={`community-filter-${t.key}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                  active ? "border-foreground bg-foreground text-background"
                         : "border-foreground/20 bg-background hover:border-foreground"
                }`}
              >
                <Icon size={14} weight="fill" style={{ color: active ? "currentColor" : t.color }} />
                {t.label}
                {t.ttl && <span className="opacity-60">·{t.ttl}</span>}
              </button>
            );
          })}
          <div className="ml-auto shrink-0">
            <button
              type="button"
              onClick={() => setShowMap((s) => !s)}
              data-testid="community-map-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-foreground font-mono text-[11px] uppercase tracking-wider hover:bg-foreground hover:text-background"
            >
              <PinIcon size={14} weight="fill" />
              {showMap ? "Hide map" : "Map"}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-3 sm:px-6 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main feed column */}
        <div className="lg:col-span-2 space-y-3 max-w-[640px] w-full mx-auto lg:mx-0">
          {/* Inline composer trigger (Facebook style) */}
          <ComposerTrigger user={user} onOpen={() => setShowComposer(true)} onSignIn={() => navigate("/")} />

          {loading && (
            <div className="border-2 border-foreground bg-background p-8 font-mono text-xs text-foreground/60 text-center">
              Loading feed…
            </div>
          )}
          {!loading && posts.length === 0 && (
            <div className="border-2 border-dashed border-foreground/40 p-8 text-center" data-testid="community-empty">
              <Newspaper size={32} weight="fill" className="mx-auto text-foreground/40" />
              <div className="font-display text-xl mt-2">No posts yet</div>
              <div className="font-mono text-xs text-foreground/60 mt-1">Be the first to share something.</div>
            </div>
          )}
          {!loading && posts.map((p) => (
            <PostCard
              key={p.post_id}
              post={p}
              onUpvote={() => upvote(p.post_id)}
              onDelete={() => removePost(p.post_id)}
              onLocate={p.location?.lat ? () => {
                setMapCenter(p.location);
                setShowMap(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
              } : null}
              canDelete={!!(user && (user.role === "admin" || (p.type !== "safety" && user.user_id === p.user_id)))}
              currentUser={user}
            />
          ))}
        </div>

        {/* Side column — Map + tips (desktop only or when toggled) */}
        <aside
          className={`${showMap ? "block" : "hidden lg:block"} space-y-3 lg:sticky lg:top-32 lg:self-start`}
          data-testid="community-aside"
        >
          <div className="border-2 border-foreground bg-background overflow-hidden">
            <div className="px-3 py-2 border-b-2 border-foreground flex items-center justify-between bg-foreground text-background">
              <div className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5">
                <PinIcon size={12} weight="fill" /> Live pins
              </div>
              <span className="font-mono text-[10px]">{pinnable.length}</span>
            </div>
            <div className="h-[280px] sm:h-[340px]" data-testid="community-map">
              <MapContainer center={[mapCenter.lat, mapCenter.lng]} zoom={11} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {pinnable.map((p) => (
                  <Marker key={p.post_id} position={[p.location.lat, p.location.lng]} icon={buildPinIcon(p.type)}>
                    <Popup>
                      <div className="leading-tight max-w-[200px]">
                        <div className="font-semibold capitalize">{p.type}</div>
                        {p.text && <div className="text-xs mt-0.5">{p.text}</div>}
                        {p.location.address && <div className="text-[10px] opacity-60 mt-0.5">{p.location.address}</div>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          <div className="border-2 border-foreground bg-background p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mb-2">Post types</div>
            <div className="space-y-1.5">
              {TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.key} className="flex items-start gap-2 text-xs">
                    <div
                      className="w-5 h-5 shrink-0 inline-flex items-center justify-center border-2 border-foreground"
                      style={{ background: t.color, color: "#fff" }}
                    >
                      <Icon size={11} weight="fill" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">
                        {t.label}
                        {t.ttl && <span className="font-mono text-[10px] opacity-60 ml-1">· {t.ttl}</span>}
                      </div>
                      <div className="text-foreground/60">{t.hint}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
      </main>

      {showComposer && (
        <Composer
          onClose={() => setShowComposer(false)}
          onCreated={(post) => {
            setShowComposer(false);
            setPosts((prev) => [post, ...prev]);
            toast.success("Posted!");
          }}
        />
      )}
    </div>
  );
}


// ---------- Inline composer trigger (Facebook-style) ----------
function ComposerTrigger({ user, onOpen, onSignIn }) {
  if (!user) {
    return (
      <div className="border-2 border-foreground bg-background p-4 flex items-center gap-3" data-testid="community-trigger-signedout">
        <Newspaper size={20} weight="fill" className="text-primary" />
        <div className="flex-1 text-sm font-medium">Sign in to post to the community</div>
        <Button onClick={onSignIn} size="sm" className="rounded-none bg-foreground text-background hover:bg-foreground/90" data-testid="community-signin-to-post">
          Sign in
        </Button>
      </div>
    );
  }
  return (
    <div className="border-2 border-foreground bg-background p-3" data-testid="community-trigger">
      <div className="flex items-center gap-3">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-10 h-10 object-cover border-2 border-foreground" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-10 h-10 bg-foreground text-background inline-flex items-center justify-center font-display">
            {(user.name || "U").charAt(0).toUpperCase()}
          </div>
        )}
        <button
          onClick={onOpen}
          data-testid="community-open-composer"
          className="flex-1 text-left px-4 py-2.5 border-2 border-foreground/30 hover:border-foreground bg-card text-foreground/60 text-sm rounded-none"
        >
          What&apos;s on your mind, <span className="font-semibold text-foreground">{(user.name || "").split(" ")[0]}</span>?
        </button>
      </div>
      <div className="grid grid-cols-3 mt-3 border-t-2 border-foreground/20 pt-2 gap-1">
        <button onClick={onOpen} data-testid="community-trigger-photo" className="flex items-center justify-center gap-1.5 py-2 hover:bg-foreground/5 font-mono text-[10px] uppercase tracking-wider">
          <ImageSquare size={16} weight="fill" className="text-accent" /> Photo
        </button>
        <button onClick={onOpen} data-testid="community-trigger-video" className="flex items-center justify-center gap-1.5 py-2 hover:bg-foreground/5 font-mono text-[10px] uppercase tracking-wider">
          <VideoCamera size={16} weight="fill" className="text-destructive" /> Video
        </button>
        <button onClick={onOpen} data-testid="community-trigger-tag" className="flex items-center justify-center gap-1.5 py-2 hover:bg-foreground/5 font-mono text-[10px] uppercase tracking-wider">
          <ShieldWarning size={16} weight="fill" className="text-primary" /> Cop alert
        </button>
      </div>
    </div>
  );
}


// ---------- Post card ----------
function PostCard({ post, onUpvote, onDelete, onLocate, canDelete, currentUser }) {
  const t = TYPE_BY_KEY[post.type] || TYPE_BY_KEY.news;
  const Icon = t.icon;
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [count, setCount] = useState(post.comment_count || 0);

  const openComments = async () => {
    setShowComments((s) => !s);
    if (comments == null) {
      try {
        const { data } = await api.get(`/community/posts/${post.post_id}/comments`);
        setComments(data || []);
        setCount(data?.length ?? count);
      } catch {
        setComments([]);
      }
    }
  };

  const submitComment = async (e) => {
    e?.preventDefault?.();
    if (!currentUser) { toast.error("Sign in to comment"); return; }
    const txt = draft.trim();
    if (!txt) return;
    setPosting(true);
    try {
      const form = new FormData();
      form.append("text", txt);
      const { data } = await api.post(`/community/posts/${post.post_id}/comments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setComments((prev) => [...(prev || []), data]);
      setCount((c) => c + 1);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comment failed");
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/community/posts/${post.post_id}/comments/${commentId}`);
      setComments((prev) => (prev || []).filter((c) => c.comment_id !== commentId));
      setCount((c) => Math.max(0, c - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <article
      data-testid={`community-post-${post.post_id}`}
      className={
        post.type === "safety"
          ? "border-2 border-destructive bg-destructive/5 shadow-[3px_3px_0_0_#E11D48]"
          : "border-2 border-foreground bg-background"
      }
    >
      {/* Safety banner — only rendered for auto speed alerts */}
      {post.type === "safety" && (
        <div
          data-testid={`community-post-safety-banner-${post.post_id}`}
          className="bg-destructive text-destructive-foreground px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"
        >
          <WarningOctagon size={12} weight="fill" />
          <span>Auto speed alert</span>
          {typeof post.speed_kmh === "number" && (
            <span className="ml-auto bg-background text-destructive px-1.5 py-0.5 leading-none">
              {Math.round(post.speed_kmh)} km/h
            </span>
          )}
        </div>
      )}
      {/* Header */}
      <header className="flex items-center gap-3 p-3">
        <AuthorAvatar author={post} size={40} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm leading-tight truncate flex items-center gap-1.5">
            <AuthorName author={post} />
            <span className="font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 bg-foreground text-background">
              {post.user_role}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-foreground/60 font-mono">
            <span>{timeAgo(post.created_at)} ago</span>
            <span>·</span>
            <span
              className="inline-flex items-center gap-0.5 uppercase tracking-wider"
              style={{ color: t.color }}
            >
              <Icon size={10} weight="fill" /> {t.label}
            </span>
          </div>
        </div>
        {canDelete && (
          <button
            onClick={onDelete}
            data-testid={`community-post-delete-${post.post_id}`}
            className="text-foreground/50 hover:text-destructive p-1"
            title="Delete"
          >
            <Trash size={16} />
          </button>
        )}
      </header>

      {/* Body text */}
      {post.text && (
        <div className="px-3 pb-2 whitespace-pre-wrap break-words text-[15px] leading-relaxed">
          {post.text}
        </div>
      )}

      {/* Media — full bleed for a single item, grid for multi-upload */}
      {(post.media_items?.length > 0) && (
        <PostMediaGrid post={post} />
      )}

      {/* Location row */}
      {post.location?.address && (
        <button
          onClick={onLocate}
          disabled={!onLocate}
          data-testid={`community-post-locate-${post.post_id}`}
          className="w-full px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-foreground/70 hover:bg-foreground/5 inline-flex items-center gap-1.5 disabled:cursor-default"
        >
          <PinIcon size={12} weight="fill" /> {post.location.address}
        </button>
      )}

      {/* Stats line */}
      {(post.upvotes > 0 || count > 0) && (
        <div className="px-3 py-1.5 text-[11px] text-foreground/60 font-mono flex items-center justify-between border-t-2 border-foreground/10">
          <span>{post.upvotes > 0 && `${post.upvotes} like${post.upvotes === 1 ? "" : "s"}`}</span>
          <button onClick={openComments} className="hover:underline" data-testid={`community-post-show-comments-${post.post_id}`}>
            {count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : ""}
          </button>
        </div>
      )}

      {/* Action bar — Facebook style */}
      <div className="grid grid-cols-2 border-t-2 border-foreground/20">
        <button
          onClick={onUpvote}
          data-testid={`community-post-upvote-${post.post_id}`}
          className={`flex items-center justify-center gap-2 py-2.5 font-mono text-[11px] uppercase tracking-wider hover:bg-foreground/5 ${
            post.upvoted ? "text-primary font-bold" : "text-foreground/70"
          }`}
        >
          <ArrowFatUp size={16} weight={post.upvoted ? "fill" : "regular"} />
          Like
        </button>
        <button
          onClick={openComments}
          data-testid={`community-post-comment-${post.post_id}`}
          className="flex items-center justify-center gap-2 py-2.5 font-mono text-[11px] uppercase tracking-wider text-foreground/70 hover:bg-foreground/5"
        >
          <ChatCircle size={16} weight={showComments ? "fill" : "regular"} />
          Comment
        </button>
      </div>

      {/* Comment thread */}
      {showComments && (
        <div className="border-t-2 border-foreground/20 p-3 space-y-2" data-testid={`community-post-comments-${post.post_id}`}>
          {comments == null && (
            <div className="font-mono text-[11px] text-foreground/50 text-center py-2">Loading…</div>
          )}
          {comments?.map((c) => (
            <div key={c.comment_id} className="flex items-start gap-2" data-testid={`community-comment-${c.comment_id}`}>
              <AuthorAvatar author={c} size={28} />
              <div className="flex-1 min-w-0">
                <div className="bg-card border-2 border-foreground/20 px-3 py-2 rounded-none">
                  <div className="text-xs font-semibold flex items-center gap-1.5">
                    <AuthorName author={c} />
                    {c.user_role && (
                      <span className="font-mono text-[8px] uppercase tracking-wider px-1 py-px bg-foreground text-background">
                        {c.user_role}
                      </span>
                    )}
                  </div>
                  <div className="text-sm mt-0.5 whitespace-pre-wrap break-words">{c.text}</div>
                </div>
                <div className="text-[10px] text-foreground/50 font-mono mt-1 px-2 flex items-center gap-2">
                  <span>{timeAgo(c.created_at)} ago</span>
                  {currentUser && (currentUser.user_id === c.user_id || currentUser.role === "admin") && (
                    <button
                      onClick={() => deleteComment(c.comment_id)}
                      className="hover:text-destructive"
                      data-testid={`community-comment-delete-${c.comment_id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {currentUser ? (
            <form onSubmit={submitComment} className="flex items-center gap-2 pt-2">
              {currentUser.picture ? (
                <img src={currentUser.picture} alt="" className="w-7 h-7 object-cover border-2 border-foreground shrink-0" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-7 h-7 bg-foreground text-background inline-flex items-center justify-center font-display text-xs shrink-0">
                  {(currentUser.name || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a comment…"
                maxLength={500}
                data-testid={`community-comment-input-${post.post_id}`}
                className="flex-1 px-3 py-2 border-2 border-foreground bg-card text-sm focus:outline-none focus:bg-background"
              />
              <button
                type="submit"
                disabled={posting || !draft.trim()}
                data-testid={`community-comment-submit-${post.post_id}`}
                className="p-2 border-2 border-foreground bg-foreground text-background disabled:opacity-40 hover:bg-foreground/90"
              >
                <PaperPlaneRight size={14} weight="fill" />
              </button>
            </form>
          ) : (
            <div className="font-mono text-[11px] text-foreground/50 text-center py-2">Sign in to comment</div>
          )}
        </div>
      )}
    </article>
  );
}


// ---------- Author link helpers ----------
// Wraps the author's avatar/name in a `<Link>` to their public profile
// (`/d/{id}` for drivers, `/r/{id}` for riders). Admin authors render plain
// (no profile page). All targeting the same `profileHref` resolver at top.

function PostMediaGrid({ post }) {
  const items = post.media_items || [];
  const [lightbox, setLightbox] = useState(null); // starting index, null = closed
  const total = items.length;
  const mediaUrlFor = (p) => `${API}/public/media/${p}`;
  if (total === 0) return null;
  // Single item → full-bleed original layout (larger and immersive).
  if (total === 1) {
    const m = items[0];
    const isVideo = m.kind === "video";
    return (
      <>
        <div className="border-y-2 border-foreground bg-foreground relative group" data-testid={`community-post-media-${post.post_id}`}>
          {isVideo ? (
            <button
              type="button"
              onClick={() => setLightbox(0)}
              className="relative block w-full text-left cursor-pointer"
              data-testid={`community-post-media-tile-${post.post_id}-0`}
            >
              {m.poster_path ? (
                <img
                  src={mediaUrlFor(m.poster_path)}
                  alt=""
                  className="w-full max-h-[560px] object-contain block bg-foreground"
                />
              ) : (
                <video
                  src={`${mediaUrlFor(m.storage_path)}#t=0.1`}
                  playsInline
                  muted
                  preload="metadata"
                  className="w-full max-h-[560px] block bg-foreground pointer-events-none"
                />
              )}
              <VideoPlayOverlay />
            </button>
          ) : (
            <img
              src={mediaUrlFor(m.storage_path)}
              alt=""
              onClick={() => setLightbox(0)}
              className="w-full max-h-[560px] object-contain block bg-foreground cursor-pointer"
              data-testid={`community-post-media-tile-${post.post_id}-0`}
            />
          )}
        </div>
        <MediaLightbox
          items={items}
          index={lightbox}
          mediaUrl={mediaUrlFor}
          onClose={() => setLightbox(null)}
          onIndexChange={setLightbox}
        />
      </>
    );
  }

  // Multi-upload: FB/IG-style 2-column or 3-column grid, capped at 4 visible.
  // Extra items surface a "+N more" overlay on the last visible tile.
  const visible = items.slice(0, 4);
  const extra = total - visible.length;
  const cols = total === 2 ? "grid-cols-2" : "grid-cols-2";
  return (
    <>
      <div
        className={`border-y-2 border-foreground bg-foreground grid gap-1 ${cols}`}
        data-testid={`community-post-media-${post.post_id}`}
      >
        {visible.map((m, i) => {
          const isVideo = m.kind === "video";
          const showOverlay = extra > 0 && i === visible.length - 1;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(i)}
              data-testid={`community-post-media-tile-${post.post_id}-${i}`}
              className="relative aspect-square bg-foreground overflow-hidden text-left cursor-pointer group"
            >
              {isVideo ? (
                m.poster_path ? (
                  <img src={mediaUrlFor(m.poster_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <video src={`${mediaUrlFor(m.storage_path)}#t=0.1`} playsInline muted preload="metadata"
                         className="w-full h-full object-cover pointer-events-none" />
                )
              ) : (
                <img src={mediaUrlFor(m.storage_path)} alt="" className="w-full h-full object-cover" />
              )}
              {isVideo && <VideoPlayOverlay compact />}
              {showOverlay && (
                <div
                  className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-mono text-3xl"
                  data-testid={`community-post-media-more-${post.post_id}`}
                >
                  +{extra}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <MediaLightbox
        items={items}
        index={lightbox}
        mediaUrl={mediaUrlFor}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
      />
    </>
  );
}

function VideoPlayOverlay({ compact = false }) {
  const size = compact ? "w-12 h-12" : "w-16 h-16";
  const arrowStyle = compact
    ? { borderTop: "8px solid transparent", borderBottom: "8px solid transparent", borderLeft: "13px solid white", marginLeft: "3px" }
    : { borderTop: "12px solid transparent", borderBottom: "12px solid transparent", borderLeft: "20px solid white", marginLeft: "5px" };
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className={`${size} rounded-full bg-foreground/80 border-2 border-background flex items-center justify-center`}>
        <div className="w-0 h-0" style={arrowStyle} />
      </div>
    </div>
  );
}

function AuthorAvatar({ author, size = 40 }) {
  const href = profileHref(author);
  const cls = `object-cover border-2 border-foreground shrink-0`;
  const style = { width: size, height: size };
  const initialCls = `bg-foreground text-background inline-flex items-center justify-center font-display shrink-0`;
  const initial = (author?.user_name || "U").charAt(0).toUpperCase();

  const body = author?.user_picture ? (
    <img
      src={author.user_picture}
      alt=""
      className={cls}
      style={style}
      referrerPolicy="no-referrer"
    />
  ) : (
    <div className={`${initialCls} border-2 border-foreground`} style={{ ...style, fontSize: size * 0.4 }}>
      {initial}
    </div>
  );

  if (!href) return body;
  return (
    <Link
      to={href}
      data-testid={`community-author-avatar-${author.user_id}`}
      className="shrink-0"
      title={`View ${author.user_name}'s profile`}
    >
      {body}
    </Link>
  );
}

function AuthorName({ author }) {
  const href = profileHref(author);
  const name = author?.user_name || "Anonymous";
  if (!href) return <span>{name}</span>;
  return (
    <Link
      to={href}
      data-testid={`community-author-name-${author.user_id}`}
      className="hover:underline"
    >
      {name}
    </Link>
  );
}


// ---------- Full-screen composer modal ----------
function Composer({ onClose, onCreated }) {
  const [type, setType] = useState("news");
  const [text, setText] = useState("");
  // Multiple attachments. Each entry: { id, file, preview: {kind, url} }.
  // Up to MAX_FILES total (enforced on the backend too).
  const [attachments, setAttachments] = useState([]);
  const [loc, setLoc] = useState(null);
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILES = 10;

  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  }, []);

  // Revoke object URLs on unmount so we don't leak video previews.
  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.preview?.kind === "video" && a.preview?.url?.startsWith("blob:")) {
          URL.revokeObjectURL(a.preview.url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_FILES - attachments.length;
    if (remaining <= 0) {
      toast.error(`You can only attach up to ${MAX_FILES} files per post`);
      return;
    }
    const toAdd = files.slice(0, remaining).filter((f) => {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name}: too large (max 25MB)`);
        return false;
      }
      return true;
    });
    if (files.length > remaining) {
      toast.error(`Only added ${remaining} of ${files.length} — max ${MAX_FILES} per post`);
    }
    const newAttachments = toAdd.map((f) => {
      const preview = f.type.startsWith("video/")
        ? { kind: "video", url: URL.createObjectURL(f) }
        : { kind: "image", url: URL.createObjectURL(f) };
      return { id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 6)}`, file: f, preview };
    });
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.preview?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(target.preview.url);
      }
      return prev.filter((a) => a.id !== id);
    });
  };

  const submit = async () => {
    if (type !== "news" && !loc) { toast.error("Waiting for your location…"); return; }
    if (!text.trim() && attachments.length === 0) { toast.error("Add a note or a photo/video"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("type", type);
      form.append("text", text.trim());
      if (loc) { form.append("lat", loc.lat); form.append("lng", loc.lng); }
      form.append("address", address.trim());
      attachments.forEach((a) => form.append("files", a.file));
      const { data } = await api.post("/community/posts", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  const tMeta = TYPE_BY_KEY[type];
  const TIcon = tMeta?.icon;

  return (
    <div className="fixed inset-0 z-[600] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="community-composer" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border-2 border-foreground w-full sm:max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b-2 border-foreground">
          <div className="font-display text-xl">Create post</div>
          <button onClick={onClose} data-testid="community-composer-close" className="w-8 h-8 border-2 border-foreground inline-flex items-center justify-center">
            <CloseIcon size={14} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Type pills */}
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = type === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  data-testid={`community-composer-type-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-foreground font-mono text-[10px] uppercase tracking-wider transition-colors ${
                    active ? "text-background" : "bg-background hover:bg-foreground/5"
                  }`}
                  style={active ? { backgroundColor: t.color } : {}}
                >
                  <Icon size={12} weight="fill" />
                  {t.label}
                </button>
              );
            })}
          </div>
          {tMeta && (
            <div className="flex items-center gap-2 text-xs text-foreground/60 -mt-1">
              {TIcon && <TIcon size={12} weight="fill" style={{ color: tMeta.color }} />}
              {tMeta.hint}
            </div>
          )}

          {/* Textarea */}
          <Textarea
            data-testid="community-composer-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder={
              type === "news" ? "Share what's happening in Guyana…"
              : type === "police" ? "e.g. Cops parked near Bourda Market"
              : type === "place" ? "Pin a spot worth visiting…"
              : type === "hazard" ? "Describe the road hazard…"
              : "Tell people about this event…"
            }
            className="rounded-none border-2 border-foreground text-base"
          />
          <div className="text-right font-mono text-[10px] text-foreground/40 -mt-2">{text.length}/500</div>

          {/* Media previews (grid, up to 10) */}
          {attachments.length > 0 && (
            <div
              className={`grid gap-2 ${attachments.length === 1 ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-3"}`}
              data-testid="community-composer-previews"
            >
              {attachments.map((a) => (
                <div key={a.id} className="border-2 border-foreground relative aspect-square bg-foreground">
                  <button
                    onClick={() => removeAttachment(a.id)}
                    className="absolute top-1 right-1 z-10 w-7 h-7 bg-background border-2 border-foreground inline-flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
                    data-testid={`community-composer-preview-remove-${a.id}`}
                    title="Remove"
                  >
                    <CloseIcon size={12} weight="bold" />
                  </button>
                  {a.preview.kind === "video" ? (
                    <video src={a.preview.url} className="w-full h-full object-cover pointer-events-none" muted />
                  ) : (
                    <img src={a.preview.url} alt="preview" className="w-full h-full object-cover" />
                  )}
                </div>
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="font-mono text-[10px] text-foreground/50 -mt-1">
              {attachments.length}/{MAX_FILES} attached
            </div>
          )}

          {/* Location label */}
          {type !== "news" && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mb-1">
                Location label
              </div>
              <input
                data-testid="community-composer-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="e.g. Sheriff St near Sportshall"
                className="w-full px-3 py-2 border-2 border-foreground bg-card font-mono text-sm focus:outline-none focus:bg-background"
              />
              <div className="font-mono text-[10px] text-foreground/50 mt-1">
                GPS: {loc ? `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}` : "Waiting…"}
              </div>
            </div>
          )}

          {/* Add-to-post bar */}
          <div className="border-2 border-foreground/20 p-3 flex items-center justify-between gap-2">
            <div className="text-xs font-semibold">Add to your post</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="community-composer-pick-photo"
                className="w-9 h-9 inline-flex items-center justify-center border-2 border-foreground bg-background hover:bg-foreground hover:text-background"
                title="Add photo"
              >
                <ImageSquare size={16} weight="fill" />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="community-composer-pick-video"
                className="w-9 h-9 inline-flex items-center justify-center border-2 border-foreground bg-background hover:bg-foreground hover:text-background"
                title="Add video"
              >
                <VideoCamera size={16} weight="fill" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/webm"
                multiple
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                className="hidden"
                data-testid="community-composer-file-input"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t-2 border-foreground sticky bottom-0 bg-background">
          <Button
            onClick={submit}
            disabled={submitting || (type !== "news" && !loc) || (!text.trim() && attachments.length === 0)}
            data-testid="community-composer-submit"
            className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40 h-11"
          >
            {submitting ? "Posting…" : `Post${attachments.length > 1 ? ` (${attachments.length} media)` : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
